import { IVec3 } from "@faro-lotv/ielement-types";
import { areaOfClosedPolygon, polygonPerimeter } from "@faro-lotv/lotv";
import { convertIElementToThreePosition } from "@faro-lotv/project-source";
import { Matrix4, Matrix4Tuple, Vector3 } from "three";

/**
 * Get the coordinates string "x, y, z" in Z-up for a given point in ProjectAPI-Y-up
 *
 * @param point the given coordinate in Y-up
 * @returns a point of the coordinates in Z-up.
 */
export function getCoordinateZUp(point: IVec3): IVec3 {
  return { x: point.x, y: point.z, z: point.y };
}

/** Props for the `getMeasurements` function */
interface GetMeasurementsProps {
  /** 3D points in local space */
  localPoints: IVec3[];

  /** Whether the 3D points form a closed loop or not */
  isClosed: boolean;

  /** World 4x4 matrix transform tuple of the points local space */
  worldMatrix4Tuple: Matrix4Tuple;
}

/** Return object of the `getMeasurements` function */
interface GetMeasurements {
  /** Total length of the measure polygon */
  length: number;

  /** Area of the measure polygon, available when `isClosed` is true */
  area?: number;

  /** Horizontal distance of the measure polygon, only available if it's a 2-point measurement */
  horizontal?: number;

  /** Vertical distance of the measure polygon, only available if it's a 2-point measurement */
  vertical?: number;

  /** Distance along the X axis, only available if it's a 2-point measurement */
  x?: number;

  /** Distance along the Y axis, only available if it's a 2-point measurement */
  y?: number;

  /** Distance along the Z axis, only available if it's a 2-point measurement */
  z?: number;
}

/**
 * Gets the measurement values from a series of 3D points
 * @returns An object with the measurement values in meters (or square meters for area)
 */
export function getMeasurements({
  localPoints,
  isClosed,
  worldMatrix4Tuple,
}: GetMeasurementsProps): GetMeasurements {
  const worldMatrix4 = new Matrix4().fromArray(worldMatrix4Tuple);

  // Convert each local 3D point to a Vector3 in world space.
  const worldPoints = localPoints.map((localPoint) => {
    const worldPoint = new Vector3()
      // Convert each point from left-handed coordinate system (ProjectAPI Y-up)
      // to right-handed coordinate system (ThreeJS Z-up)
      .fromArray(convertIElementToThreePosition(localPoint))
      .applyMatrix4(worldMatrix4);
    return worldPoint;
  });

  const length = polygonPerimeter(worldPoints, isClosed);
  const area = isClosed ? areaOfClosedPolygon(worldPoints).area : undefined;

  let horizontal = undefined;
  let vertical = undefined;
  let x = undefined;
  let y = undefined;
  let z = undefined;

  if (worldPoints.length === 2) {
    const [p1, p2] = worldPoints;
    horizontal = new Vector3(p1.x, p2.y, p1.z).distanceTo(
      new Vector3(p2.x, p2.y, p2.z)
    );
    vertical = new Vector3(p1.x, p1.y, p1.z).distanceTo(
      new Vector3(p1.x, p2.y, p1.z)
    );
    x = new Vector3(p1.x, p2.y, p2.z).distanceTo(new Vector3(p2.x, p2.y, p2.z));
    y = new Vector3(p1.x, p2.y, p1.z).distanceTo(new Vector3(p1.x, p2.y, p2.z));
    z = new Vector3(p1.x, p1.y, p1.z).distanceTo(new Vector3(p1.x, p2.y, p1.z));
  }

  return {
    length,
    area,
    horizontal,
    vertical,
    x,
    y,
    z,
  };
}
